import EmptyState from 'components/EmptyState/EmptyState';
import { ErrorMessage } from 'components/Widgets/ErrorMessage';
import WidgetLoadingAnimation from 'components/Widgets/WidgetLoadingAnimation';
import WidgetTimeUpdate from 'components/Widgets/WidgetTimeUpdate';
import WeatherMapResizableLayout from 'features/Weather/features/WeatherMaps/components/WeatherMapResizableLayout/WeatherMapResizableLayout';
import useLocation from 'hooks/useLocation';
import useWindow from 'hooks/useWindow';
import { useState } from 'react';
import './assets/hurricanes.scss';
import HurricaneInformationPanel from './components/HurricaneInformationPanel/HurricaneInformationPanel';
import HurricaneMapContent from './components/HurricaneMapContent/HurricaneMapContent';
import HurricanesPageContainer from './components/HurricanesPageContainer/HurricanesPageContainer';
import {
  HURRICANES_DEFAULT_REPLAY_MARKER_MODE,
  HURRICANES_PAGE_LAYOUT,
  HURRICANES_REFRESH_INTERVAL_MINUTES,
  HURRICANES_ROUTE,
} from './config/hurricanes.config';
import useHurricaneData from './hooks/useHurricaneData';
import useStormReplay from './hooks/useStormReplay';
import useStormSelection from './hooks/useStormSelection';
import { toLatLng } from './utils/hurricaneGeometry.utils';
import { getStormRecordAtFrame } from './utils/hurricanes.utils';

// Full-page Hurricanes & Tropical Storms view rendered by the Maps
// "Hurricane" tab and the Storm Center "Hurricanes and Tropical Storms" tab.
const Hurricanes = () => {
  const { locations } = useLocation();
  const { width } = useWindow();
  const effectiveLocation = locations.effectiveLocation;
  const isDesktop = width >= 992;
  const {
    liveStormGroups,
    historicalStormGroups,
    hasLiveStorms,
    isLiveLoading,
    isHistoricalLoading,
    liveError,
    historicalError,
    isLoading,
    isRefreshing,
    isError,
    refetch,
  } = useHurricaneData();

  const {
    dataset,
    stormGroups,
    selectedStormKey,
    selectedStorm,
    visibleStormGroups,
    selectStorm,
    setDatasetToHistorical,
  } = useStormSelection({ liveStormGroups, historicalStormGroups, hasLiveStorms });

  const replay = useStormReplay({
    frameCount: selectedStorm?.records.length ?? 0,
    resetKey: selectedStormKey,
  });
  const [replayMarkerMode, setReplayMarkerMode] = useState(HURRICANES_DEFAULT_REPLAY_MARKER_MODE);
  const activeRecord = getStormRecordAtFrame(selectedStorm?.records, replay.frameIndex);
  const hasNoStormData = liveStormGroups.length === 0 && historicalStormGroups.length === 0;
  const isDatasetToggleDisabled =
    !hasLiveStorms || isLiveLoading || isHistoricalLoading || liveError || historicalError;

  if (isError) {
    return (
      <HurricanesPageContainer isDesktop={isDesktop}>
        <ErrorMessage message="Unable to load hurricane data." onRetry={refetch} />
      </HurricanesPageContainer>
    );
  }

  if (isLoading) {
    return (
      <HurricanesPageContainer isDesktop={isDesktop}>
        <WidgetLoadingAnimation />
      </HurricanesPageContainer>
    );
  }

  if (hasNoStormData) {
    return (
      <HurricanesPageContainer isDesktop={isDesktop}>
        <EmptyState
          title="Hurricanes & Tropical Storms"
          message="No hurricane or tropical storm data is available right now."
        />
      </HurricanesPageContainer>
    );
  }

  const mapContent = (
    <HurricaneMapContent
      stormGroups={visibleStormGroups}
      selectedStorm={selectedStorm}
      dataset={dataset}
      frameIndex={selectedStorm ? replay.frameIndex : null}
      markerMode={replayMarkerMode}
      fallbackCenter={toLatLng(effectiveLocation?.coords?.[0], effectiveLocation?.coords?.[1])}
      scrollZoom={isDesktop}
    />
  );
  const informationPanelContent = (
    <HurricaneInformationPanel
      stormGroups={stormGroups}
      selectedStormKey={selectedStormKey}
      selectedStorm={selectedStorm}
      activeRecord={activeRecord}
      dataset={dataset}
      hasLiveStorms={hasLiveStorms}
      liveError={liveError}
      historicalError={historicalError}
      isRefreshing={isRefreshing}
      isDatasetToggleDisabled={isDatasetToggleDisabled}
      replay={replay}
      markerMode={replayMarkerMode}
      onSelectStorm={selectStorm}
      onDatasetChange={setDatasetToHistorical}
      onMarkerModeChange={setReplayMarkerMode}
    />
  );

  return (
    <HurricanesPageContainer isDesktop={isDesktop}>
      <WidgetTimeUpdate
        route={HURRICANES_ROUTE}
        updateIntervalBasis={HURRICANES_REFRESH_INTERVAL_MINUTES}
      />

      <WeatherMapResizableLayout
        {...HURRICANES_PAGE_LAYOUT}
        isResizable={isDesktop}
        mapAriaLabel="Hurricane and tropical storm map"
        panelAriaLabel="Storm information and replay controls"
        resizeHandleAriaLabel="Resize storm map and information"
        containerClassName={`hurricanes-content${isDesktop ? ' flex-grow-1' : ' gap-3'}`}
        mapClassName="hurricanes-map-pane"
        panelClassName="hurricanes-information-pane"
        mapContent={mapContent}
        panelContent={informationPanelContent}
      />
    </HurricanesPageContainer>
  );
};

export default Hurricanes;
